import React, { useState, useEffect, useCallback } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import realApiService from '../../utils/realApiService';
import { useUserProfile } from '../../hooks/useUserProfile';

const Billing = () => {
  const { userProfile, currentOrganization: profileOrg } = useUserProfile();
  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // Remove default fallback
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState({
    subscription: {},
    paymentHistory: [],
    usage: {},
    plans: [],
  });
  const [recentInvoices, setRecentInvoices] = useState([]);

  // Active tab state
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'CreditCard' },
    { id: 'subscription', label: 'Subscription', icon: 'Package' },
    { id: 'usage', label: 'Usage', icon: 'BarChart3' },
    { id: 'history', label: 'Payment History', icon: 'Receipt' },
    { id: 'plans', label: 'Plans & Pricing', icon: 'Layers' },
  ];

  useEffect(() => {
    // derive user and role from unified profile
    if (userProfile) {
      setCurrentUser(userProfile);
      // Only set role if it exists - no default fallback
      if (userProfile.role) {
        setUserRole(userProfile.role.toLowerCase());
      } else {
        console.error('User profile missing role:', userProfile);
        setUserRole(null);
      }
    }

    // Set current organization from profile
    if (profileOrg) {
      setCurrentOrganization(profileOrg);
    }
  }, [userProfile, profileOrg]);

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);

      // Real API calls
      const [subscriptionResp, paymentHistoryResp, dashboardResp, plansResp] = await Promise.all([
        realApiService.billing.getSubscription().catch(() => null),
        realApiService.billing.getPaymentHistory({ limit: 50 }).catch(() => []),
        realApiService.billing.getDashboard().catch(() => null),
        realApiService.billing.getPlans().catch(() => ({ plans: [] })),
      ]);

      // Map subscription
      const subscription = subscriptionResp
        ? {
            plan: (subscriptionResp.tier || '').toString().toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            status: (subscriptionResp.status || '').toString().toLowerCase(),
            price: Number(subscriptionResp.monthly_price || 0),
            currency: 'INR',
            billingCycle: (subscriptionResp.billing_cycle || 'monthly').toLowerCase(),
            nextBillingDate: subscriptionResp.next_billing_date || subscriptionResp.current_period_end,
            seats: subscriptionResp.max_team_members ?? 0,
            usedSeats: dashboardResp?.usage?.active_users ?? 0,
            features: [],
          }
        : {
            plan: 'Free',
            status: 'active',
            price: 0,
            currency: 'INR',
            billingCycle: 'monthly',
            nextBillingDate: null,
            seats: 5,
            usedSeats: dashboardResp?.usage?.active_users ?? 0,
            features: [],
          };

      // Map payment history
      const paymentHistory = Array.isArray(paymentHistoryResp)
        ? paymentHistoryResp.map(p => ({
            id: String(p.id),
            date: p.payment_date || p.created_at || new Date().toISOString(),
            amount: p.amount ?? 0,
            status: (p.status || '').toString().toLowerCase() === 'succeeded' ? 'paid' : (p.status || '').toString().toLowerCase(),
            description: p.description || 'Subscription Charge',
            downloadUrl: '#',
          }))
        : [];

      // Map usage from dashboard
      const usage = dashboardResp?.usage
        ? {
            currentPeriod: {
              start: dashboardResp.subscription?.current_period_start || new Date().toISOString(),
              end: dashboardResp.subscription?.current_period_end || new Date().toISOString(),
            },
            metrics: [
              { name: 'Active Users', current: dashboardResp.usage.active_users, limit: dashboardResp.usage.max_users, unit: 'users' },
              { name: 'Projects', current: dashboardResp.usage.projects, limit: dashboardResp.usage.max_projects ?? 'unlimited', unit: 'projects' },
              { name: 'Storage', current: dashboardResp.usage.storage_gb, limit: dashboardResp.usage.max_storage_gb, unit: 'GB' },
              { name: 'API Calls', current: dashboardResp.usage.api_calls, limit: dashboardResp.usage.max_api_calls, unit: 'calls' },
            ],
            overageCharges: 0,
          }
        : { currentPeriod: null, metrics: [], overageCharges: 0 };

      // Map plans
      const plans = Array.isArray(plansResp?.plans)
        ? plansResp.plans.map(pl => ({
            id: String(pl.tier).toLowerCase(),
            name: pl.name,
            price: pl.monthly_price,
            currency: 'INR',
            billingCycle: 'monthly',
            features: pl.features || [],
            current: (subscriptionResp?.tier && pl.tier === subscriptionResp.tier) || false,
            popular: false,
          }))
        : [];

      setBillingData({ subscription, paymentHistory, usage, plans });
      setRecentInvoices(Array.isArray(dashboardResp?.recent_invoices) ? dashboardResp.recent_invoices : []);
    } catch (error) {
      console.error('Failed to load billing data:', error);
      // No mock fallback - show empty data
      setBillingData({ subscription: { plan: '', status: '', price: 0, billingCycle: '', nextBillingDate: null, seats: 0, usedSeats: 0, features: [] }, paymentHistory: [], usage: { currentPeriod: null, metrics: [], overageCharges: 0 }, plans: [] });
      setRecentInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  // Mock data generators
  // No mock generators: show empty UI when backend has no data

  const handleDownloadInvoice = useCallback(async () => {
    try {
      if (!recentInvoices || recentInvoices.length === 0) return;
      const invoiceId = recentInvoices[0]?.id;
      if (!invoiceId) return;

      // Ensure PDF is generated, then download
      await realApiService.billing.generateInvoicePDF(invoiceId).catch(() => {});
      const blob = await realApiService.billing.downloadInvoicePDF(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download invoice:', e);
    }
  }, [recentInvoices]);

  // Check if user has access to billing - only allow owners
  if (!userRole || userRole !== 'owner') {
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader
          userRole={userRole ? userRole.toLowerCase() : 'member'}
          currentUser={currentUser}
          currentOrganization={profileOrg}
        />
        <main className='pt-16'>
          <div className='max-w-7xl mx-auto p-6'>
            <div className='flex items-center justify-center h-64'>
              <div className='text-center'>
                <Icon
                  name='Lock'
                  size={32}
                  className='mx-auto mb-4 text-text-secondary'
                />
                <h3 className='text-lg font-medium text-text-primary mb-2'>
                  Access Restricted
                </h3>
                <p className='text-text-secondary mb-4'>
                  Billing management is only available to Organization Owners.
                </p>
                <Button onClick={() => window.history.back()}>Go Back</Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );

  const renderSubscriptionTab = () => (
    <div className='space-y-6'>
      <div className='bg-card rounded-lg border border-border p-6'>
        <h3 className='text-lg font-semibold text-text-primary mb-4'>Subscription Details</h3>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          <div>
            <p className='text-sm text-text-secondary'>Plan</p>
            <p className='text-xl font-bold text-text-primary'>{billingData.subscription.plan || 'Free'}</p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Status</p>
            <p className='text-xl font-bold text-text-primary'>{billingData.subscription.status || 'active'}</p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Billing Cycle</p>
            <p className='text-xl font-bold text-text-primary'>{billingData.subscription.billingCycle || 'monthly'}</p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Price</p>
            <p className='text-xl font-bold text-text-primary'>₹{billingData.subscription.price || 0}/{billingData.subscription.billingCycle || 'monthly'}</p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Seats</p>
            <p className='text-xl font-bold text-text-primary'>{billingData.subscription.usedSeats || 0} / {billingData.subscription.seats || 0}</p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Next Billing</p>
            <p className='text-xl font-bold text-text-primary'>{billingData.subscription.nextBillingDate ? new Date(billingData.subscription.nextBillingDate).toLocaleDateString() : '—'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsageTab = () => (
    <div className='space-y-6'>
      <div className='bg-card rounded-lg border border-border p-6'>
        <h3 className='text-lg font-semibold text-text-primary mb-4'>Usage</h3>
        {billingData.usage?.metrics?.length ? (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
            {billingData.usage.metrics.map((metric, idx) => (
              <div key={idx} className='p-4 bg-muted rounded-lg'>
                <p className='text-sm text-text-secondary'>{metric.name}</p>
                <div className='mt-2'>
                  <div className='flex items-center justify-between'>
                    <span className='text-lg font-bold text-text-primary'>
                      {metric.current} {metric.unit}
                    </span>
                    <span className='text-sm text-text-secondary'>
                      / {metric.limit === 'unlimited' ? '∞' : `${metric.limit} ${metric.unit}`}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='text-text-secondary'>No usage data available.</div>
        )}
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className='space-y-6'>
      <div className='bg-card rounded-lg border border-border p-6'>
        <h3 className='text-lg font-semibold text-text-primary mb-4'>Payment History</h3>
        {billingData.paymentHistory?.length ? (
          <div className='overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead>
                <tr className='text-left text-text-secondary'>
                  <th className='px-2 py-2'>Date</th>
                  <th className='px-2 py-2'>Description</th>
                  <th className='px-2 py-2'>Amount</th>
                  <th className='px-2 py-2'>Status</th>
                </tr>
              </thead>
              <tbody>
                {billingData.paymentHistory.map(p => (
                  <tr key={p.id} className='border-t border-border'>
                    <td className='px-2 py-2 whitespace-nowrap'>{new Date(p.date).toLocaleDateString()}</td>
                    <td className='px-2 py-2 whitespace-nowrap'>{p.description}</td>
                    <td className='px-2 py-2 whitespace-nowrap'>₹{p.amount}</td>
                    <td className='px-2 py-2 whitespace-nowrap capitalize'>{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='text-text-secondary'>No payments yet.</div>
        )}
      </div>
    </div>
  );
  }

  const renderOverviewTab = () => (
    <div className='space-y-6'>
      {/* Current Subscription */}
      <div className='bg-card rounded-lg border border-border p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-text-primary'>
            Current Subscription
          </h3>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              billingData.subscription.status === 'active'
                ? 'bg-success/10 text-success'
                : 'bg-destructive/10 text-destructive'
            }`}
          >
            {billingData.subscription.status}
          </span>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
          <div>
            <p className='text-sm text-text-secondary'>Plan</p>
            <p className='text-xl font-bold text-text-primary'>
              {billingData.subscription.plan}
            </p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Price</p>
            <p className='text-xl font-bold text-text-primary'>
              ₹{billingData.subscription.price}/
              {billingData.subscription.billingCycle}
            </p>
          </div>
          <div>
            <p className='text-sm text-text-secondary'>Next Billing</p>
            <p className='text-xl font-bold text-text-primary'>
              {billingData.subscription.nextBillingDate
                ? new Date(billingData.subscription.nextBillingDate).toLocaleDateString()
                : '—'}
            </p>
          </div>
        </div>

        <div className='mt-6 flex space-x-4'>
          <Button variant='outline' onClick={() => setActiveTab('plans')}>
            Change Plan
          </Button>
          <Button variant='outline'>Update Payment Method</Button>
        </div>
      </div>

      {/* Usage Overview */}
      <div className='bg-card rounded-lg border border-border p-6'>
        <h3 className='text-lg font-semibold text-text-primary mb-4'>
          Usage Overview
        </h3>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
          {billingData.usage.metrics.map((metric, index) => (
            <div key={index} className='p-4 bg-muted rounded-lg'>
              <p className='text-sm text-text-secondary'>{metric.name}</p>
              <div className='mt-2'>
                <div className='flex items-center justify-between'>
                  <span className='text-lg font-bold text-text-primary'>
                    {metric.current} {metric.unit}
                  </span>
                  <span className='text-sm text-text-secondary'>
                    /{' '}
                    {metric.limit === 'unlimited'
                      ? '∞'
                      : `${metric.limit} ${metric.unit}`}
                  </span>
                </div>
                {metric.limit !== 'unlimited' && (
                  <div className='mt-2 w-full bg-background rounded-full h-2'>
                    <div
                      className='bg-primary h-2 rounded-full'
                      style={{
                        width: `${(metric.current / metric.limit) * 100}%`,
                      }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Payments */}
      <div className='bg-card rounded-lg border border-border p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-text-primary'>
            Recent Payments
          </h3>
          <Button variant='outline' onClick={() => setActiveTab('history')}>
            View All
          </Button>
        </div>

        <div className='space-y-3'>
          {billingData.paymentHistory.slice(0, 3).map((payment) => (
            <div
              key={payment.id}
              className='flex items-center justify-between p-3 bg-muted rounded-lg'
            >
              <div>
                <p className='font-medium text-text-primary'>
                  {payment.description}
                </p>
                <p className='text-sm text-text-secondary'>
                  {new Date(payment.date).toLocaleDateString()}
                </p>
              </div>
              <div className='text-right'>
                <p className='font-bold text-text-primary'>₹{payment.amount}</p>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    payment.status === 'paid'
                      ? 'bg-success/10 text-success'
                      : 'bg-warning/10 text-warning'
                  }`}
                >
                  {payment.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPlansTab = () => (
    <div className='space-y-6'>
      <div className='text-center mb-8'>
        <h3 className='text-2xl font-bold text-text-primary mb-2'>
          Choose Your Plan
        </h3>
        <p className='text-text-secondary'>
          Select the plan that best fits your organization's needs
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {billingData.plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-card rounded-lg border p-6 ${
              plan.current
                ? 'border-primary ring-2 ring-primary/20'
                : 'border-border'
            } ${plan.popular ? 'ring-2 ring-accent/20' : ''}`}
          >
            {plan.popular && (
              <div className='absolute -top-3 left-1/2 transform -translate-x-1/2'>
                <span className='bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-medium'>
                  Most Popular
                </span>
              </div>
            )}

            <div className='text-center mb-6'>
              <h4 className='text-xl font-bold text-text-primary'>
                {plan.name}
              </h4>
              <div className='mt-2'>
                <span className='text-3xl font-bold text-text-primary'>
                  ₹{plan.price}
                </span>
                <span className='text-text-secondary'>
                  /{plan.billingCycle}
                </span>
              </div>
            </div>

            <ul className='space-y-3 mb-6'>
              {plan.features.map((feature, index) => (
                <li key={index} className='flex items-center'>
                  <Icon name='Check' size={16} className='text-success mr-2' />
                  <span className='text-sm text-text-secondary'>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              variant={plan.current ? 'outline' : 'default'}
              className='w-full'
              disabled={plan.current}
            >
              {plan.current ? 'Current Plan' : 'Upgrade to ' + plan.name}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className='min-h-screen bg-background'>
      <RoleBasedHeader
        userRole={userRole.toLowerCase()}
        currentUser={
          currentUser
            ? {
                name: `${currentUser.firstName} ${currentUser.lastName}`,
                email: currentUser.email,
                role: userRole,
              }
            : {
                name: 'Loading...',
                email: '',
                role: userRole,
              }
        }
        currentOrganization={profileOrg}
      />

      <main className='pt-16'>
        <div className='max-w-7xl mx-auto p-6'>
          <Breadcrumb />

          {/* Page Header */}
          <div className='flex items-center justify-between mb-8'>
            <div>
              <h1 className='text-3xl font-bold text-text-primary'>
                Billing & Subscription
              </h1>
              <p className='text-text-secondary mt-2'>
                Manage your subscription, view usage, and payment history
                {currentOrganization && (
                  <span className='ml-2 text-primary'>
                    for {currentOrganization.name}
                  </span>
                )}
              </p>
            </div>

            <Button
              variant='outline'
              iconName='Download'
              onClick={handleDownloadInvoice}
              disabled={loading || !recentInvoices || recentInvoices.length === 0}
              title={(!recentInvoices || recentInvoices.length === 0) ? 'No invoices available' : 'Download latest invoice'}
            >
              Download Invoice
            </Button>
          </div>

          {/* Tab Navigation */}
          <div className='border-b border-border mb-8'>
            <nav className='flex space-x-8'>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {loading ? (
            <div className='flex items-center justify-center h-64'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4'></div>
                <p className='text-text-secondary'>Loading billing data...</p>
              </div>
            </div>
          ) : (
            <div>
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'plans' && renderPlansTab()}
              {/* Other tabs would be implemented here */}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Billing;
